import * as _ from 'underscore';
import { getAppStore } from '../../app/AppStore';
import { SessionServerSelectors } from '../../app/selectors/SessionInfoSelectors';
import { Formatters, Messages } from '../../codegen/Localize';
import { FeatureFlags } from '../FeatureFlags';
import { L10n } from '../L10n/L10n';
import { IScheduleInfo } from '../../schedule/types/IScheduleInfo';
import { Module } from '../Module';
import { DefinedBy, IDailySchedule, IHourlySchedule, IMonthlySchedule, ISchedule, IWeeklySchedule, ScheduleDaysOfTheWeek, ScheduleLastDayOfMonth, ScheduleSecondToLastDayOfMonth, ScheduleTypes } from '../../schedule/types/ISchedule';
import { SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { formatListAsString } from '../../schedule/utils/ListToStringFormatter';


export interface IScheduleFrequencyDescriptionFilter {
  (schedule: ISchedule, enforceLegacy?: boolean, clientTimezoneOffsetOverride?: number): string;
}

const minutesPerDay = 60 * 24;
const dayNames = [
  ScheduleDaysOfTheWeek.Sun,
  ScheduleDaysOfTheWeek.Mon,
  ScheduleDaysOfTheWeek.Tue,
  ScheduleDaysOfTheWeek.Wed,
  ScheduleDaysOfTheWeek.Thu,
  ScheduleDaysOfTheWeek.Fri,
  ScheduleDaysOfTheWeek.Sat
];

function formatTime(minutesAfterMidnight: number) {
  const date = new Date();
  date.setHours(minutesAfterMidnight / 60);
  date.setMinutes(minutesAfterMidnight % 60);
  return L10n.localizedShortTime(date);
}

function computeTimeOffset(inputTimeMinutes: number, diffMinutes: number) {
  let newTimeMinutes = inputTimeMinutes + diffMinutes;
  if (newTimeMinutes > minutesPerDay) {
    newTimeMinutes = newTimeMinutes % minutesPerDay;
  } else if (newTimeMinutes < 0) {
    newTimeMinutes = minutesPerDay + newTimeMinutes;
  }

  return newTimeMinutes;
}

//When considering timezone difference, enough offset could move day forward or backward
//e.g. 10PM on Monday somewhere could be 2AM on Tuesday somewhere else
function computeDayOffset(startTime: number, diffMinutes: number) {
  const newStart = startTime + diffMinutes;
  let dayOffset = 0;
  if (newStart > minutesPerDay) {
    dayOffset = 1;
  } else if (newStart < 0) {
    dayOffset = -1;
  }

  return dayOffset;
}

function hourlyFrequencyForAllDayHelper(recurrenceMinutes: number, minutesAfterHour: number, startTime: number) {
  const frequency = recurrenceMinutes/60;
  if(minutesAfterHour === 0){
    if (recurrenceMinutes < 60) {
      return Messages.scheduleEveryXMinutesDescription({
        MINUTES: Formatters.formatCount(recurrenceMinutes)
      });
    } else if (frequency === 1) {
      return Messages.scheduleEveryOneHourDescription();
    } else {
      return Messages.scheduleEveryXHoursStartingAtYDescription({
        HOURS: Formatters.formatCount(frequency),
        START: formatTime(startTime)
      });
    }
  } else if(minutesAfterHour === 15) {
    if (recurrenceMinutes < 60) {
      if (recurrenceMinutes === 30) {
        return Messages.scheduleEvery30MinutesAtYDescription();
      } else {
        return Messages.scheduleEveryXMinutesDescription({
          MINUTES: Formatters.formatCount(recurrenceMinutes),
        });
      }
    } else {
      if (frequency === 1) {
        return Messages.scheduleEveryOneHourAtXMinutesPastDescription({
          MINUTES: Formatters.formatCount(minutesAfterHour),
        });
      } else {
        return Messages.scheduleEveryXHoursStartingAtYDescription({
          HOURS: Formatters.formatCount(frequency),
          START: formatTime(startTime),
        });
      }
    }
  } else if (minutesAfterHour === 30) {
    if (recurrenceMinutes < 60) {
      return Messages.scheduleEveryXMinutesDescription({
        MINUTES: Formatters.formatCount(recurrenceMinutes)
      });
    } else {
      if (frequency === 1) {
        return Messages.scheduleEveryOneHourAtXMinutesPastDescription({
          MINUTES: Formatters.formatCount(minutesAfterHour)
        });
      } else {
        return Messages.scheduleEveryXHoursStartingAtYDescription({
          HOURS: Formatters.formatCount(frequency),
          START: formatTime(startTime),
        });
      }
    }
  } else if (minutesAfterHour === 45) {
    if (recurrenceMinutes < 60) {
      if (recurrenceMinutes === 30) {
        return Messages.scheduleEvery30MinutesAtYDescription();
      } else {
        return Messages.scheduleEveryXMinutesDescription({
          MINUTES: Formatters.formatCount(recurrenceMinutes),
        });
      }
    } else {
      if (frequency === 1) {
        return Messages.scheduleEveryOneHourAtXMinutesPastDescription({
          MINUTES: Formatters.formatCount(minutesAfterHour),
        });
      } else {
        return Messages.scheduleEveryXHoursStartingAtYDescription({
          HOURS: Formatters.formatCount(frequency),
          START: formatTime(startTime)
        });
      }
    }
  } else {
    return Messages.scheduleEveryXHoursStartingAtYDescription({
      HOURS: Formatters.formatCount(frequency),
      START: formatTime(startTime)
    });
  }
}

function formatHourly(schedule: ISchedule, startTime: number, diffMinutes: number, dayOffset: number, enforceLegacy?: boolean) {
  const hourly = <IHourlySchedule>schedule.scheduleDetails;
  const newEnd = computeTimeOffset(hourly.endTime, diffMinutes);
  let recurrenceDaysOfWeek = hourly.recurrenceDaysOfWeek;
  let scheduleFrequency: string;

  const forceEndTimePresence = FeatureFlags.adminSelfServiceSchedulesEnabled && !enforceLegacy;
  if(startTime === newEnd && schedule.definedBy !== DefinedBy.SELF_SERVICE && !forceEndTimePresence){
    scheduleFrequency = hourlyFrequencyForAllDayHelper(hourly.recurrenceMinutes, startTime % 60, startTime);
  } else {
    const frequency = hourly.recurrenceMinutes < 60
        ? Messages.minutes({
            MINUTES: hourly.recurrenceMinutes,
            MINUTES_LOC: Formatters.formatCount(hourly.recurrenceMinutes),
          })
        : Messages.hours({
            HOURS: hourly.recurrenceMinutes / 60,
            HOURS_LOC: Formatters.formatCount(hourly.recurrenceMinutes / 60),
          });

    scheduleFrequency = Messages.scheduleHourlyDescription({
      FREQUENCY: frequency,
      START: formatTime(startTime),
      END: formatTime(newEnd)
    });
  }

  if (recurrenceDaysOfWeek === undefined || recurrenceDaysOfWeek.length === 0) {
    return scheduleFrequency;
  } else {
    if (dayOffset !== 0) {
      recurrenceDaysOfWeek = shiftDayOfWeek(hourly.recurrenceDaysOfWeek, dayOffset);
    }
    const days = _.map(recurrenceDaysOfWeek, dayName);

    return Messages.scheduleDescriptionWithWeekdays({
      SCHEDULE: scheduleFrequency,
      DAYS: formatListAsString(days)
    });
  }
}

function formatDaily(schedule: ISchedule, startTime: number, diffMinutes: number, dayOffset: number, enforceLegacy?: boolean) {
  const daily = <IDailySchedule>schedule.scheduleDetails;
  if (daily.endTime !== undefined
      || daily.recurrenceDaysOfWeek !== undefined
      || daily.recurrenceMinutes !== undefined){
      return formatHourly (schedule, startTime, diffMinutes, dayOffset, enforceLegacy);
  } else {
      return Messages.scheduleDailyDescription({
        START: formatTime(startTime)
      });
  }
}

function formatWeekly(schedule: ISchedule, newStartTime: number, dayOffset: number) {
  const weekly = <IWeeklySchedule>schedule.scheduleDetails;
  let recurrenceDaysOfWeek = weekly.recurrenceDaysOfWeek;
  if (dayOffset !== 0) {
    recurrenceDaysOfWeek = shiftDayOfWeek(weekly.recurrenceDaysOfWeek, dayOffset);
  }
  const days = _.map(recurrenceDaysOfWeek, dayName);
  return Messages.scheduleWeeklyDescription({
    START: formatTime(newStartTime),
    DAYS: formatListAsString(days)
  });
}

const DayNameForCode: _.Dictionary<() => string> = {
  Sat: Messages.dayNameSat,
  Sun: Messages.dayNameSun,
  Mon: Messages.dayNameMon,
  Tue: Messages.dayNameTue,
  Wed: Messages.dayNameWed,
  Thu: Messages.dayNameThu,
  Fri: Messages.dayNameFri,
};

function dayName(dayCode: string): string {
  return DayNameForCode[dayCode]() || '';
}

function formatMonthly(schedule: ISchedule, newStartTime: number, dayOffset: number) {
  const monthly = <IMonthlySchedule>schedule.scheduleDetails;
  let recurrenceDayOfMonth = monthly.recurrenceDayOfMonth;
  let recurrenceDaysOfWeek = monthly.recurrenceDaysOfWeek;

  if (recurrenceDaysOfWeek === undefined || recurrenceDaysOfWeek.length === 0) {
    if (schedule.definedBy === DefinedBy.SELF_SERVICE || FeatureFlags.adminSelfServiceSchedulesEnabled) {
      const recurrenceDaysOfMonthArrNum = IScheduleInfo.toDaysOfMonthArr(recurrenceDayOfMonth);
      const recurrenceDaysOfMonthArr: string[] = [];

      for (let i = 0; i < recurrenceDaysOfMonthArrNum.length; i++) {
        let dayNum = recurrenceDaysOfMonthArrNum[i].toString();
        if (dayOffset !== 0) {
          dayNum = shiftDayOfMonth(dayNum, dayOffset);
        }
        recurrenceDaysOfMonthArr[i] = dayNum;
      }

      const daysOfMonth: string[] = [];
      for (let j = 0; j < recurrenceDaysOfMonthArr.length; j++) {
        // this can be "0" in the case of no shifting
        if (recurrenceDaysOfMonthArr[j] === '0' || recurrenceDaysOfMonthArr[j] === ScheduleLastDayOfMonth) {
          daysOfMonth.push(Messages.last());
        } else if (recurrenceDaysOfMonthArr[j] === ScheduleSecondToLastDayOfMonth) {
          daysOfMonth.push(Messages.secondToLast());
        } else {
          const day = parseInt(recurrenceDaysOfMonthArr[j], 10);
          daysOfMonth.push(Messages.dayOfMonth({
            DAY: day,
            DAY_LOC: Formatters.formatCount(day),
          }));
        }
      }

      return Messages.scheduleMonthlyDescription({
        START: formatTime(newStartTime),
        DAYOFMONTH: formatListAsString(daysOfMonth)
      });

    } else {
      //The case of Nth day in the month
      if (dayOffset !== 0) {
        recurrenceDayOfMonth = shiftDayOfMonth(monthly.recurrenceDayOfMonth, dayOffset);
      }

      if (recurrenceDayOfMonth === ScheduleLastDayOfMonth) {
        return Messages.scheduleMonthlyDescriptionLast({START: formatTime(newStartTime)});
      } else if (recurrenceDayOfMonth === ScheduleSecondToLastDayOfMonth) {
        return Messages.scheduleMonthlyDescriptionSecondToLast({START: formatTime(newStartTime)});
      } else {
        const day = parseInt(recurrenceDayOfMonth, 10);
        const dayOfMonth = Messages.dayOfMonth({
          DAY: day,
          DAY_LOC: Formatters.formatCount(day)
        });
        return Messages.scheduleMonthlyDescription({
          START: formatTime(newStartTime),
          DAYOFMONTH: dayOfMonth
        });
      }
    }
  } else {
    //The case of Nth day of week in the month
    //Story 580849
    if (dayOffset !== 0) {
      recurrenceDaysOfWeek = shiftDayOfWeek(monthly.recurrenceDaysOfWeek, dayOffset);
    }
    const days = _.map(recurrenceDaysOfWeek, dayName);
    const dayNum = IScheduleInfo.toDaysOfMonthArr(recurrenceDayOfMonth);

    if (dayNum.length !== 1) {
      throw 'when choosing Nth day of week in the month, there should only be one "day of month". Found ' + dayNum.length;
    }

    const day = dayNum[0];
    let weekdayIndex = Messages.dayOfMonth({
      DAY: day,
      DAY_LOC: Formatters.formatCount(day)
    });

    if (dayNum[0] === 0) {
      weekdayIndex = Messages.last();
    }

    return Messages.scheduleMonthlyDescriptionWeekday({
      START: formatTime(newStartTime),
      NTH: weekdayIndex,
      WEEKDAY: days[0]
    });
  }
}

function shiftDayOfMonth(recurrenceDayOfMonth: string, dayOffset: number) {
  //Gets the number of days in the current month
  const d = new Date();
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  if (recurrenceDayOfMonth === '0' || recurrenceDayOfMonth === ScheduleLastDayOfMonth) {
    if (dayOffset > 0) {
      return '1';
    } else if (dayOffset < 0) {
      return ScheduleSecondToLastDayOfMonth;
    } else {
      return ScheduleLastDayOfMonth;
    }
  }

  if ((recurrenceDayOfMonth === '1') && (dayOffset < 0)) {
    return ScheduleLastDayOfMonth;
  }
  const index = +recurrenceDayOfMonth;
  const shiftedIndex = (index + dayOffset + daysInMonth) % daysInMonth;
  return String(shiftedIndex);
}

function shiftDayOfWeek(recurrenceDaysOfWeek: string[], dayOffset: number) {
  const releventDaysOfWeek: string[] = [];
  for (let i = 0; i < recurrenceDaysOfWeek.length; i++) {
    const dayIndex = dayNames.indexOf(recurrenceDaysOfWeek[i]);
    const shiftedDayIndex = (dayIndex + dayOffset + dayNames.length) % dayNames.length;
    releventDaysOfWeek.push(dayNames[shiftedDayIndex]);
  }

  return releventDaysOfWeek;
}

export function ScheduleFrequencyDescriptionFilterFromSessionInfo(displaySchedulesInClientTimezone: boolean) {
  const serverServiceMock = {
    displaySchedulesInClientTimezone: () => displaySchedulesInClientTimezone
  } as SessionManagementService;

  return ScheduleFrequencyDescriptionFilterFactory(serverServiceMock);
}

export function describeLegacyAdminSchedule(schedule: ISchedule, clientTimezoneOffsetOverride?: number): string {
  return ScheduleFrequencyDescriptionFilterFromSessionInfo(SessionServerSelectors.displaySchedulesInClientTimezone(getAppStore().getState()))(schedule, true, clientTimezoneOffsetOverride);
}

export function ScheduleFrequencyDescriptionFilterFactory(serverService: SessionManagementService): IScheduleFrequencyDescriptionFilter {
  return function scheduleFrequencyDescriptionFilter(schedule: ISchedule, enforceLegacy?: boolean, clientTimezoneOffsetOverride?: number) {
    const d = new Date();
    const clientTimeZoneOffset = clientTimezoneOffsetOverride || (0 - d.getTimezoneOffset());
    let diffMinutes = 0;
    let newStartTime = schedule.scheduleDetails.startTime;
    let dayOffset = 0;
    let formattedDescription;

    // when it is a self service schedule, do not convert time
    const displaySchedulesInClientTimezone = serverService.displaySchedulesInClientTimezone();
    if (displaySchedulesInClientTimezone === true && schedule.definedBy !== DefinedBy.SELF_SERVICE) {
      diffMinutes = clientTimeZoneOffset - schedule.serverTimezoneOffset;
      newStartTime = computeTimeOffset(schedule.scheduleDetails.startTime, diffMinutes);
      dayOffset = computeDayOffset(schedule.scheduleDetails.startTime, diffMinutes);
    }

    // TFS 1096704: this function should only ever deal with legacy schedules
    // When Admin SSS is enabled, localize using SSS
    if (FeatureFlags.adminSelfServiceSchedulesEnabled && !enforceLegacy) {
      return IScheduleInfo.getScheduleDescription(schedule);
    }

    switch(schedule.scheduleType) {

      case ScheduleTypes.Hourly:
        formattedDescription = formatHourly(schedule, newStartTime, diffMinutes, dayOffset, enforceLegacy);
        break;

      case ScheduleTypes.Daily:
        formattedDescription = formatDaily(schedule, newStartTime, diffMinutes, dayOffset, enforceLegacy);
        break;

      case ScheduleTypes.Weekly:
        formattedDescription = formatWeekly(schedule, newStartTime, dayOffset);
        break;

      case ScheduleTypes.Monthly:
        formattedDescription = formatMonthly(schedule, newStartTime, dayOffset);
        break;

      default:
        throw 'Unknown schedule type: ' + schedule.scheduleType;
    }

    // show time zone display name only when is it a self service schedule
    // null and undefined check with juggling-check
    if (schedule.definedBy === DefinedBy.SELF_SERVICE
        && schedule.timeZoneDisplayName != null)
      return formattedDescription + ' ' + schedule.timeZoneDisplayName;
    return formattedDescription;
  };
}

Module.instance().filter('scheduleFrequencyDescription', [
  (
  ): IScheduleFrequencyDescriptionFilter => {
    return (schedule: ISchedule, enforceLegacy?: boolean, clientTimezoneOffsetOverride?: number) => IScheduleInfo.getScheduleDescription(schedule, clientTimezoneOffsetOverride, enforceLegacy || undefined);
  }
]);
